import { logger } from "@/common/logger";
import { useEffect, useLayoutEffect, useReducer, useState } from "react";
import { graphql, requestSubscription, useRelayEnvironment } from "react-relay";
import {
  WorkspaceRunnerPhase,
  workspaceRunnerStatusSubscription,
} from "./__generated__/workspaceRunnerStatusSubscription.graphql";
import { sleep } from "./helpers";
import { object, string } from "valibot";

export type { WorkspaceRunnerPhase };

type RunnerShape = { id: ID; phase: WorkspaceRunnerPhase; url: string };
type ReadyRunner = { phase: "READY" };

export function useReadyWorkspaceRunner<
  T extends RunnerShape | null | undefined,
>(runner: T): runner is T & ReadyRunner {
  const relay = useRelayEnvironment();
  const [error, setError] = useState<unknown | null>(null);

  const runnerId = runner?.id;
  const runnerPhase = runner?.phase;
  const runnerUrl = runner?.url;

  useEffect(() => {
    if (!runnerId || runnerPhase === "READY") return;

    const subscription = requestSubscription<workspaceRunnerStatusSubscription>(
      relay,
      {
        subscription: Subscription,
        variables: { id: runnerId },
        onError: setError,
      },
    );

    return () => subscription.dispose();
  }, [relay, runnerId, runnerPhase]);

  interface State {
    runner: T | null | undefined;
    isHealthy: boolean;
  }
  type Action =
    | { kind: "set_healthy" }
    | { kind: "change_runner"; runner: T | null | undefined };
  const [{ isHealthy }, dispatch] = useReducer(
    (state: Readonly<State>, action: Readonly<Action>): Readonly<State> => {
      switch (action.kind) {
        case "set_healthy":
          return { ...state, isHealthy: true };
        case "change_runner":
          if (
            !!action.runner &&
            !!state.runner &&
            action.runner.id === state.runner.id &&
            action.runner.url === state.runner.url
          ) {
            // runner did not change
            return {
              ...state,
              runner: action.runner,
            };
          } else {
            // runner changed: never assume health from the phase alone. READY
            // means the pod's conditions are True, not that the ingress route
            // is programmed — a claimed warm pod's first observation is
            // already READY while its Ingress was created moments ago — so
            // every runner earns isHealthy through one untilHealthy probe.
            return {
              runner: action.runner,
              isHealthy: false,
            };
          }
      }
    },
    undefined,
    (): Readonly<State> => ({
      runner,
      isHealthy: false,
    }),
  );

  /* eslint-disable react-hooks/exhaustive-deps */
  useLayoutEffect(() => {
    dispatch({ kind: "change_runner", runner });
    setError(null);
  }, [runnerId, runnerUrl, runnerPhase]);
  /* eslint-enable react-hooks/exhaustive-deps */

  useEffect(() => {
    if (isHealthy) return;
    if (runnerPhase !== "READY") return;
    if (!runnerUrl) return;

    const abort = new AbortController();

    void (async () => {
      await untilHealthy(runnerUrl, abort.signal, backoff());
      dispatch({ kind: "set_healthy" });
    })().catch((error) => {
      if (!abort.signal.aborted) {
        setError(error);
      }
    });

    return () => abort.abort();
  }, [isHealthy, runnerUrl, runnerPhase]);

  if (error !== null) {
    throw error;
  }

  return !!runner && runner.phase === "READY" && isHealthy;
}

const Subscription = graphql`
  subscription workspaceRunnerStatusSubscription($id: ID!) {
    workspaceRunnerStatus(id: $id) {
      phase
      url
    }
  }
`;

/** The editor url pointed at `file`, or unchanged when no file is selected. */
export function withNotebookFile(
  editorUrl: string,
  file: string | null,
): string {
  if (file === null) return editorUrl;
  const url = new URL(editorUrl);
  url.searchParams.set("file", file);
  return url.toString();
}

async function untilHealthy(
  target: string,
  signal: AbortSignal,
  backoff: Iterator<number, void, never>,
): Promise<void> {
  let lastError = "Healthiness never checked";

  {
    const url = new URL(target);
    const pathname = url.pathname.split("/");
    url.search = "";
    url.pathname = pathname.slice(0, -1).join("/") + "/health";
    target = url.toString();
  }

  while (!signal.aborted) {
    const start = performance.now();

    const timeout = backoff.next();
    if (timeout.done) {
      lastError = "Timed out checking healthiness";
      break;
    }

    let response;
    const timeoutSignal = AbortSignal.timeout(timeout.value);
    try {
      response = await fetch(target, {
        signal: AbortSignal.any([signal, timeoutSignal]),
      });
    } catch (error) {
      if (signal.aborted) {
        throw error;
      } else if (!timeoutSignal.aborted) {
        logger.error(error);
        lastError = String(error);
      }
    }

    if (response) {
      if (response.status >= 200 && response.status < 300) {
        return;
      } else if (response.headers.get("content-type") === "text/plain") {
        try {
          lastError = await response.text();
        } catch (error) {
          logger.error(error);
          lastError = `Unhealthy so far (status: ${response.status})`;
        }
      } else {
        lastError = `Unhealthy so far (status: ${response.status})`;
      }
    }

    const elapsed = performance.now() - start;
    if (timeout.value > elapsed) {
      await sleep(timeout.value - elapsed, signal);
    }
  }

  throw new Error(lastError);
}

function* backoff(maxWaitMillis = 120_000): Generator<number, void, never> {
  let acc = 0;
  while (true) {
    let next: number;
    if (acc < 3_000) {
      next = 600;
    } else if (acc < 10_000) {
      next = 1_000;
    } else if (acc < 45_000) {
      next = 2_000;
    } else {
      next = 3_000;
    }

    acc += next;
    if (acc >= maxWaitMillis) {
      break;
    }

    yield next;
  }
}

export const WorkspacePageParams = object({
  owner: string(),
  slug: string(),
});

export const WorkspaceVersionPageParams = object({
  owner: string(),
  slug: string(),
  version: string(),
});
